<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Crawl History</span>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>
    <transition name="slide-up">
      <div class="a-filter" v-if="!isLoading">
        <a-row class="full-w" type="flex" justify="start" :gutter="24">
          <a-col :span="8">
            <div class="full-w a-input-group" :class="{ active: !!filterParams.search }">
              <a-button size="large">
                <a-icon type="search" />
              </a-button>
              <a-input
                class="custom-prefix"
                placeholder="Search by status or domain"
                @input="onSearch"
              ></a-input>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>
    <div v-resize="elSizeChange" class="a-body flex-grow-1 overflow-y-hidden">
      <transition name="slide-up">
        <template v-if="!isLoading">
          <PaginationTable
            :client-paging="false"
            :pagination="pagin"
            :columns="CrawlHistoryColumns"
            :dataSource="filterData"
            :tableSize="tableSize"
          >
            <template slot="status" slot-scope="{ record }">
              <router-link class="nav-link-detail d-inline-block" :to="'/web-crawl/' + record.id">
                <a-tag :color="tagStatusColor(record.status)">
                  {{ record.status }}
                </a-tag>
              </router-link>
            </template>
            <template slot="domains" slot-scope="{ record }">
              <router-link class="nav-link-detail" :to="'/web-crawl/' + record.id">
                <template v-if="record.domains && record.domains.length > 0">
                  <div v-for="(d, i) in record.domains.slice(0, 3)" :key="i" class="domain-name">{{ d }}</div>
                  <a-tooltip v-if="record.domains.length > 3">
                    <template slot="title">
                      <div v-for="(d, i) in record.domains" :key="i">{{ d }}</div>
                    </template>
                    <span class="domain-more">+{{ record.domains.length - 3 }} more</span>
                  </a-tooltip>
                </template>
                <template v-else>
                  {{ record.total_jobs || 0 }} {{ (record.total_jobs || 0) === 1 ? 'domain' : 'domains' }}
                </template>
              </router-link>
            </template>
            <template slot="depth_mode" slot-scope="{ record }">
              {{ getDepthModeLabel(record.depth_mode) }}
            </template>
            <template slot="url_report_mode" slot-scope="{ record }">
              {{ getUrlReportModeLabel(record.url_report_mode) }}
            </template>
            <template slot="progress" slot-scope="{ record }">
              <span>{{ record.completed_jobs || 0 }} / {{ record.total_jobs || 0 }}</span>
            </template>
            <template slot="created_at" slot-scope="{ record }">
              <div>
                <p class="time-text">{{ record.created_at | dateString }}</p>
                <p class="time-text">{{ record.created_at | timeString }}</p>
              </div>
            </template>
            <template slot="completed_at" slot-scope="{ record }">
              <div v-if="record.completed_at">
                <p class="time-text">{{ record.completed_at | dateString }}</p>
                <p class="time-text">{{ record.completed_at | timeString }}</p>
              </div>
              <span v-else>-</span>
            </template>
            <template slot="operation" slot-scope="{ record }">
              <a-dropdown placement="topLeft">
                <a-icon class="operation" type="ellipsis" />
                <a-menu slot="overlay" class="operation-menu">
                  <a-menu-item @click="viewDetail(record)">
                    <a-icon type="eye" />
                    <span>View Detail</span>
                  </a-menu-item>
                  <a-menu-item v-if="isCancellable(record)" :disabled="cancellingCrawlId === record.id">
                    <div class="full-wh" @click="showCancelCrawlConfirm(record)">
                      <a-icon type="stop" />
                      Cancel Crawl
                    </div>
                  </a-menu-item>
                  <a-menu-item>
                    <div class="full-wh text-danger" @click="showDeleteCrawlConfirm(record)">
                      <a-icon type="delete" />
                      Delete
                    </div>
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </template>
          </PaginationTable>
        </template>
      </transition>
    </div>
    <div class="a-footer">
      <PaginationBar
        :current="pagin.current"
        :pageSize.sync="pagin.pageSize"
        :total="pagin.total"
        @change="paginChange"
      />
    </div>
  </div>
</template>

<script>
  import { debounce } from 'lodash';
  import { PAGE_OPTIONS } from '@constants/pageOptions';
  import {
    CrawlStatusEnum,
    CrawlStatusColorTag,
    getDepthModeLabel,
    getUrlReportModeLabel,
  } from '../consts/CrawlStatus.consts';
  import WebCrawlService from '../services/web-crawl.service';
  import { CrawlHistoryColumns } from '../columnsTable/CrawlHistoryColumns';
  import { formatApiError } from '../helpers/format-api-error';

  export default {
    name: 'CrawlHistory',
    data() {
      let pageSize = Number(window.localStorage.getItem('pageSize'));
      if (!pageSize) pageSize = 20;
      pageSize = Math.min(100, Math.max(1, pageSize));

      return {
        PAGE_OPTIONS,
        CrawlStatusEnum,
        CrawlHistoryColumns,
        filterData: [],
        filterParams: {
          search: '',
        },
        pagin: {
          current: 1,
          pageSize: pageSize,
          total: 0,
        },
        isLoading: true,
        cancellingCrawlId: null,
        tableSize: {
          height: '',
          width: '',
        },
      };
    },
    computed: {
      tagStatusColor() {
        return (status) => CrawlStatusColorTag[status] || 'default';
      },
      isCancellable() {
        return (record) =>
          record.status === CrawlStatusEnum.Pending || record.status === CrawlStatusEnum.Processing;
      },
    },
    mounted() {
      this.fetchList();
    },
    methods: {
      getDepthModeLabel,
      getUrlReportModeLabel,
      viewDetail(record) {
        if (!record || !record.id) return;
        this.$router.push(`/web-crawl/${record.id}`);
      },
      onSearch: debounce(function (inputChangeEvent) {
        const inputSearchValue = inputChangeEvent.target.value;
        this.filterParams.search = inputSearchValue ? String(inputSearchValue).trim().toLowerCase() : '';
        this.pagin.current = 1;
        this.fetchList(1, this.pagin.pageSize);
      }, 800),
      async fetchList(requestedPage, requestedPageSize) {
        const page = requestedPage != null ? requestedPage : this.pagin.current;
        let pageSize = requestedPageSize != null ? requestedPageSize : this.pagin.pageSize;
        pageSize = Math.min(100, Math.max(1, Number(pageSize) || 20));
        const search = (this.filterParams.search || '').trim();
        try {
          const response = await WebCrawlService.getCrawls({ page, perPage: pageSize, search });
          const crawlListData = (response && response.data) || [];
          const meta = (response && response.meta) || {};
          const total = meta.total != null ? meta.total : crawlListData.length;
          const currentPage = meta.current_page != null ? meta.current_page : page;
          const perPage = meta.per_page != null ? meta.per_page : pageSize;
          const offset = (currentPage - 1) * perPage;
          this.filterData = crawlListData.map((item, index) => ({
            ...item,
            index: offset + index + 1,
          }));
          this.pagin = {
            current: currentPage,
            pageSize: perPage,
            total,
          };
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to fetch crawl history.'));
        } finally {
          if (this.isLoading) {
            setTimeout(() => {
              this.isLoading = false;
            }, 300);
          }
        }
      },
      paginChange(pagin) {
        this.pagin = { ...pagin };
        this.fetchList(pagin.current, pagin.pageSize);
      },
      showCancelCrawlConfirm(record) {
        this.$confirm({
          title: 'Cancel this crawl?',
          content: 'The crawl will be stopped and cannot be resumed.',
          okText: 'Cancel Crawl',
          okType: 'danger',
          cancelText: 'Keep Running',
          onOk: () => this.onCancelCrawl(record),
        });
      },
      async onCancelCrawl(record) {
        if (this.cancellingCrawlId) return;
        this.cancellingCrawlId = record.id;
        try {
          await WebCrawlService.cancelCrawl(record.id);
          this.$showSuccessMessage('Crawl cancelled.');
          await this.fetchList(this.pagin.current, this.pagin.pageSize);
        } catch (error) {
          const status = error && error.response && error.response.status;
          if (status === 409) {
            this.$showErrorMessage('This crawl can no longer be cancelled.');
            await this.fetchList(this.pagin.current, this.pagin.pageSize);
          } else {
            this.$showErrorMessage(formatApiError(error, 'Failed to cancel crawl.'));
          }
        } finally {
          this.cancellingCrawlId = null;
        }
      },
      showDeleteCrawlConfirm(record) {
        this.$confirm({
          title: 'Delete this crawl?',
          content: 'This removes the crawl from your history. This action cannot be undone.',
          okText: 'Delete',
          okType: 'danger',
          cancelText: 'Cancel',
          onOk: () => this.onDeleteCrawl(record),
        });
      },
      async onDeleteCrawl(record) {
        try {
          await WebCrawlService.deleteCrawl(record.id);
          this.$showSuccessMessage('Crawl deleted.');
          const page = this.pagin.current;
          await this.fetchList(page, this.pagin.pageSize);
          if (this.filterData.length === 0 && page > 1) {
            await this.fetchList(page - 1, this.pagin.pageSize);
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to delete crawl.'));
        }
      },
      elSizeChange(size) {
        this.tableSize = size;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .time-text {
    margin-bottom: 0;
    white-space: nowrap;
  }

  .nav-link-detail {
    &:hover {
      text-decoration: underline;
    }
  }

  .domain-name {
    font-size: 13px;
    line-height: 1.4;
    word-break: break-all;
  }

  .domain-more {
    font-size: 12px;
    color: $prLgt;
  }
</style>
